import React, { useContext, useEffect, useRef, useState } from 'react';

import { media } from 'context';
import Layout from 'ds/Layout';
import { Message } from 'shared';

import SupportTicketMessage from './SupportTicketMessage';

export interface SupportTicketMessagesSubject {
  id: number;
  messages: Message[];
  title: string | null;
  ai_assistant_conversation_id?: number | null;
}

interface Props {
  suppressScroll?: boolean;
  supportTicket: SupportTicketMessagesSubject;
  onEmojiSelect: ({ emoji, messageId }: { emoji: string; messageId: number }) => void;
  onCreateLearning?: (message: Message) => void;
  supportTicketTitle?: string;
  aiAssistantConversationId?: number;
  showAbsoluteTime?: boolean;
  onToggleTimeFormat?: () => void;
  readOnly?: boolean;
}

const SupportTicketMessages: React.FC<Props> = ({
  supportTicket: { id, messages, title, ai_assistant_conversation_id },
  onEmojiSelect,
  suppressScroll,
  onCreateLearning,
  supportTicketTitle,
  aiAssistantConversationId,
  showAbsoluteTime: showAbsoluteTimeProp,
  onToggleTimeFormat: onToggleTimeFormatProp,
  readOnly = false
}) => {
  const updateKey = messages.length;
  const originalMessagesLengthRef = useRef(messages.length);
  const { isMobile } = useContext(media);
  const [showAbsoluteTimeLocal, setShowAbsoluteTimeLocal] = useState<boolean>(true);
  const showAbsoluteTime = showAbsoluteTimeProp !== undefined ? showAbsoluteTimeProp : showAbsoluteTimeLocal;
  const onToggleTimeFormat =
    onToggleTimeFormatProp !== undefined ? onToggleTimeFormatProp : () => setShowAbsoluteTimeLocal(prev => !prev);

  useEffect(() => {
    // We don't want to scroll to bottom on page load!
    if (suppressScroll || messages.length === originalMessagesLengthRef.current) return;

    window.scrollTo(0, document.body.scrollHeight);
  }, [updateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (messages.length === 0) {
    return null;
  }

  return (
    <Layout paddingTop={12} paddingBottom={24}>
      {messages.map((message, index) => {
        const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;

        const isLastMessageInGroup = nextMessage === null || nextMessage.author?.id !== message.author?.id;

        return (
          <Layout
            key={message.id}
            marginTop={!isMobile && index !== 0 ? 12 : undefined}
            marginBottom={isLastMessageInGroup ? 24 : undefined}
            borderTop={isMobile}
            paddingX={isMobile ? 24 : undefined}
            paddingTop={isMobile ? 12 : undefined}
            paddingBottom={isMobile ? 24 : undefined}
          >
            <SupportTicketMessage
              isLastMessageInGroup={isLastMessageInGroup}
              message={message}
              supportTicketId={id}
              onEmojiSelect={onEmojiSelect}
              onCreateLearning={onCreateLearning}
              supportTicketTitle={supportTicketTitle ?? title ?? undefined}
              aiAssistantConversationId={aiAssistantConversationId ?? ai_assistant_conversation_id ?? undefined}
              showAbsoluteTime={showAbsoluteTime}
              onToggleTimeFormat={onToggleTimeFormat}
              readOnly={readOnly}
            />
          </Layout>
        );
      })}
    </Layout>
  );
};

export default SupportTicketMessages;
